import styled from 'styled-components'
import { useElementSize } from 'usehooks-ts'
import * as d3 from 'd3'
import { ReactNode, useMemo, useState } from 'react'
import { ChartAnnotation } from 'components/ChartAnnotation'
import { useMFContext } from 'components/MFHelpers'

const xAxisSpace = 90
const topSpace = 80
const axisLabelHeight = 22

export const Chart = ({
  height = 600,
  data,
  yDomain = [0, 100],
  yTicks = [0, 25, 50, 75],
  yAxisWidth = 80,
  labels,
  barColor = 'var(--chart-bar-color)',
  barMarkColor = 'var(--yellow-main)',
  barspace = 1,
  Annotation,
  percentileId,
  hideCanton,
  userPos,
  reading,
}: {
  height?: number
  data: number[]
  yDomain?: number[]
  yTicks?: number[]
  yAxisWidth?: number
  labels?: {
    title?: string
    sub?: string
    xAxisTitle?: string[]
    xAxisLabels?: string[]
    yAxisTitle?: string[]
  }
  barColor?: string
  barMarkColor?: string
  barspace?: number
  Annotation?: ReactNode
  percentileId: number
  hideCanton?: boolean
  userPos?: number
  reading?: ReactNode | string
}) => {
  const [containerRef, { width=0 }] = useElementSize<HTMLElement>()
  const barCount = data.length
  const barwidth = ((width||0) / (barCount + 1)) * 0.97
  const xstart = barwidth * 0.25
  const { _t } = useMFContext()
  const [toolTipData, setToolTipData] = useState<
    { val: number; pos: { x: number; y: number }; width: number } | undefined
  >(undefined)

  const { title, sub, xAxisTitle, xAxisLabels, yAxisTitle } = labels || {}

  const chartArea = {
    top: topSpace,
    bottom: height - xAxisSpace,
    left: 0, //yAxisWidth,
    right: width,
  }

  const y = useMemo(
    () =>
      d3
        .scaleLinear()
        .range([0, chartArea.bottom - chartArea.top])
        .domain(yDomain),
    [data, chartArea.bottom, chartArea.top]
  )

  var marker_x = 0
  var marker_y = 0
  if (userPos || userPos === 0) {
    marker_x =
      xstart + userPos * barwidth + (userPos - 1) * barspace + barwidth / 2
    marker_y = chartArea.bottom - Math.max(2, y(data[userPos])) - 20
  }

  const overlay_width = 200
  const overlay_y = chartArea.top - 20

  return (
    <ChartRoot>
      <Title>{title}</Title>
      <Sub>{sub}</Sub>
      <SVGContainer
        style={{
          height: height + 'px',
        }}
        ref={containerRef}
      >
        <ChartSVG>
          <Axis
            x1={0}
            x2={width}
            y1={height - xAxisSpace + 3}
            y2={height - xAxisSpace + 3}
          />
          {yTicks.map((tickval) => {
            var yLine = height - xAxisSpace - y(tickval)
            return (
              <YTick transform={`translate(0,${yLine})`}>
                {tickval !== 0 && <line x1={0} x2={width - 10} />}
                <text y={-4}>{formatnumber(tickval)}</text>
              </YTick>
            )
          })}
          {data.map((val, i) => {
            return (
              <FullColumn
                onMouseEnter={(e: any) => setToolTipData(e)}
                x={xstart + i * barwidth + (i - 1) * barspace}
                y={chartArea.bottom - Math.max(2, y(val))}
                val={val}
                barwidth={barwidth}
                onMouseLeave={() => setToolTipData(undefined)}
                isMarked={i === userPos}
                fill={i === userPos ? barMarkColor : barColor}
                height={Math.max(2, y(val))}
                fullheight={400}
                tooltipAnchor={
                  xstart + i * barwidth + (i - 1) * barspace > width / 2
                    ? 'end'
                    : 'start'
                }
              />
            )
          })}

          {yAxisTitle && (
            <>
              <YAxisTitle
                x={1}
                y={0}
                style={{ height: '100px', width: width + 'px' }}
              >
                <div
                  style={{
                    position: 'fixed',
                    height: '100px',
                    width: width + 'px',
                  }}
                >
                  {yAxisTitle[0]}
                  <br />
                  <span>{yAxisTitle[1]}</span>
                </div>
              </YAxisTitle>
            </>
          )}
          {xAxisTitle && (
            <XAxisTitle
              x={xstart}
              y={height - xAxisSpace + 30}
              style={{ height: '60px', width: width - xstart + 'px' }}
            >
              <div style={{ position: 'fixed', width: width - xstart + 'px' }}>
                {xAxisTitle[0]}

                <span>
                    {_t('source')}:{' '}
                  <a href='https://wid.world/country/switzerland/'>World Inequality Database</a>
                </span>
              </div>
            </XAxisTitle>
          )}
          {xAxisLabels && (
            <AxisLabel x={xstart} y={height - xAxisSpace + axisLabelHeight}>
              {xAxisLabels[0]}
            </AxisLabel>
          )}
          {xAxisLabels && (
            <AxisLabel
              x={width - 10}
              y={height - xAxisSpace + axisLabelHeight}
              textAnchor='end'
            >
              {xAxisLabels[1]}
            </AxisLabel>
          )}
          {Annotation && (userPos || userPos === 0) && (
            <g
              transform={`translate(${
                marker_x > width * 0.65
                  ? marker_x -
                    overlay_width -
                    (overlay_y > marker_y ? barwidth : 0)
                  : marker_x
              },${overlay_y})`}
            ></g>
          )}
          {toolTipData && <Tooltip data={toolTipData} barWidth={barwidth} />}
        </ChartSVG>
      </SVGContainer>
      <Reading>{reading}</Reading>
    </ChartRoot>
  )
}

const FullColumn = ({
  x,
  y,
  onMouseEnter,
  onMouseLeave,
  val,
  barwidth,
  fill,
  height,
  fullheight,
  isMarked,
  tooltipAnchor,
}: {
  x: number
  y: number
  val: number
  barwidth: number
  fill: string
  height: number
  fullheight: number
  onMouseEnter: (e: {
    val: number
    pos: {
      x: number
      y: number
    }
    width: number
  }) => void
  onMouseLeave: () => void
  isMarked: boolean
  tooltipAnchor: 'start' | 'end'
}) => {
  const { _t } = useMFContext()

  return (
    <g transform={`translate(${x},${0})`}>
      <rect
        className='hoverbar'
        width={barwidth}
        height={fullheight + 80}
        fill='none'
        pointerEvents={'all'}
        y={30}
        onMouseEnter={() =>
          onMouseEnter({
            val,
            pos: {
              x: x,
              y: y,
            },
            width: barwidth,
          })
        }
        onMouseLeave={() => onMouseLeave()}
      />
      {isMarked && (
        <g transform={`translate(${0},${y - 55})`}>
          <line
            x1={barwidth / 2}
            x2={barwidth / 2}
            y1={22}
            y2={45}
            stroke={'var(--text-color)'}
            strokeWidth={1.5}
          />
          <text
            y={18}
            x={barwidth / 2}
            textAnchor='middle'
            className='usermarker'
          >
            {_t('einkommen_chart_you')}
          </text>
          <polygon
            transform={'translate(' + (barwidth / 2 - 8) + ',20)'}
            points={[
              [0, 16],
              [8, 32],
              [16, 16],
            ].join(' ')}
            fill={'var(--text-color)'}
          />
        </g>
      )}
      <Column
        y={y}
        width={barwidth}
        height={height}
        fill={fill}
        pointerEvents={'all'}
        onMouseEnter={() =>
          onMouseEnter({
            val,
            pos: {
              x: x,
              y: y,
            },
            width: barwidth,
          })
        }
        onMouseLeave={() => onMouseLeave()}
      />
    </g>
  )
}

function formatnumber(num: number) {
  if (num > 499999) {
    return ('' + Math.round(num / 100000) / 10).replace('.', ',') + ' Mio.'
  }
  if (num > 9999) {
    return Math.round(num / 1000) + '’000'
  }

  return num.toString()
}

const Tooltip = ({
  data,
  barWidth,
}: {
  data: { val: number; pos: { x: number; y: number }; width: number }
  barWidth: number
}) => {
  const { pos, val, width } = data || {}
  var rectwidth = 90
  var cirlesize = 5
  return (
    <g
      transform={`translate(${pos.x + barWidth / 2},${pos.y - 55})  `}
      style={{ pointerEvents: 'none', fontSize: '15px', fontWeight: 700 }}
    >
      <polygon
        transform='translate(-8,20)'
        points={[
          [0, 16],
          [8, 32],
          [16, 16],
        ].join(' ')}
        fill={'var(--text-color)'}
      />
      <rect
        x={-rectwidth / 2}
        width={rectwidth}
        height={40}
        fill='var(--site-background)'
        stroke='var(--text-color)'
      />
      <circle
        cx={-rectwidth / 2 + cirlesize + 10}
        cy={20}
        r={cirlesize}
        stroke='var(--text-color)'
        fill='none'
        strokeWidth={2}
      />
      <line
        x1={-rectwidth / 2 + cirlesize * 2 + 10}
        x2={-rectwidth / 2 + 10}
        y1={20 - cirlesize / 2 - 4}
        y2={20 + cirlesize / 2 + 4}
        stroke='var(--text-color)'
        strokeWidth={2}
      />

      <text
        x={cirlesize + 5}
        y={25}
        textAnchor='middle'
        style={{ fontSize: '14px', fill: 'var(--text-color)' }}
      >
        {formatnumber(Math.round(val / 100) * 100)}
      </text>
    </g>
  )
}

const Reading = styled.div`
  font-size: 18px;
  background-color: var(--yellow-main);
  color: var(--brandblue-main);
  padding: 20px;
  line-height: 1.6em;
  margin-bottom: 60px;
  border-radius: 10px;
  div {
    line-height: 1.6em;
    position: fixed;
    b {
      font-size: 14px;
    }
  }
  font-size: 18px;
  line-height: 1.6em;
  @media screen and (max-width: 711px) {
    font-size: 15px;
  }
`

const ChartRoot = styled.div`
  width: 100%;
  margin-top: 30px;
  margin-bottom: 30px;
  text.usermarker {
    font-size: 15px;
  }
`

const SVGContainer = styled.div`
  line-height: 0;
  margin-bottom: 10px;
`

const ChartSVG = styled.svg`
  overflow: visible;
  /* border: 1px solid #ddd; */
  width: 100%;
  height: 100%;
  /* .hoverbar {
    &:hover {
      opacity: 0.2;
      fill: var(--brandblue-bright-4);
    }
  } */
`

const Title = styled.div`
  flex: 0 0 100%;
  font-size: 26px;
  line-height: 1.4em;
  font-weight: 200;
  letter-spacing: 0.6px;
  text-align: center;
  margin-bottom: 5px;
  @media screen and (max-width: 712px) {
    font-size: 19px;
  }
`

const Sub = styled.div`
  font-size: 15px;
  margin-bottom: 30px;
`

const Column = styled.rect`
  pointer-events: none;

  /* fill: var(--gray-4); */
`

const Axis = styled.line`
  stroke: var(--text-color);
  stroke-width: 2;
`

const AxisLabel = styled.text`
  font-size: 13px;
  fill: var(--text-color);
`

const AxisTitle = styled.foreignObject`
  font-size: 18px;
  margin-bottom: 15px;
  font-weight: 700;
  fill: var(--text-color);
  line-height: 1.2em;

  span {
    display: inline-block;
    font-weight: 300;
    /* padding-left: 8px; */
    width: 100%;
    font-size: 12px;
    a{
      text-decoration: none;
      border-bottom: 1px solid var(--text-color);
      cursor: pointer;
      color:var(--text-color) !important;
    }
  }
`
const XAxisTitle = styled(AxisTitle)`
  text-align: left;
  font-size: 15px;
  line-height: 1.4em;
  @media screen and (max-width: 712px) {
    font-size: 13px;
  }
`
const YAxisTitle = styled(AxisTitle)`
  width: 400px;
  flex: 0 0 100%;
  font-size: 26px;
  line-height: 1.4em;
  font-weight: 200;
  letter-spacing: 0.6px;
  text-align: center;
  margin-bottom: 5px;
  @media screen and (max-width: 712px) {
    font-size: 19px;
  }
`

const YTick = styled.g`
  pointer-events: none;
  line {
    stroke: var(--chart-yticks);
  }
  text {
    font-size: 12px;
    fill: var(--text-color);
    @media screen and (max-width: 711px) {
      font-size: 10px;
    }
  }
`
